// @flow
import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import useHasPermission from '../../hooks/useHasPermission';

/**
 * Custom hook that returns info to decide if the user can perform affordable waitlist
 * actions or not
 * @returns
 */
const useAffordableWaitlistValidations = (): {
  affordableWaitlistFlag: boolean,
  hasAffordableWaitlistPermissionAdd: boolean,
  hasAffordableWaitlistPermissionReinstate: boolean,
  accountThisPropertyForWaitlist: boolean,
  canAccessManageAffordableWaitlist: boolean,
  affordableWaitlistPropertySetting: boolean | null,
  isAffordableWaitlistPropertySettingStatusClosed: 'OPEN' | 'CLOSED',
  propertyAffordablePrograms: string[],
  waitlistV2Programs: string[],
} => {
  const {
    affordableWaitlist: affordableWaitlistFlag,
    affordableWaitlistV2: affordableWaitlistV2Flag,
  } = useFlags();
  const hasAffordableWaitlistPermissionAdd = useHasPermission(
    'affordable-waitlist-add-remove-select',
  );

  // affordable-waitlist-reinstate
  const hasAffordableWaitlistPermissionReinstate = useHasPermission(
    'affordable-waitlist-reinstate',
  );

  const selectedProperty = useSelectedProperty();

  const {
    accountThisPropertyForWaitlist,
    canAccessManageAffordableWaitlist,
    affordableWaitlistPropertySetting,
    isAffordableWaitlistPropertySettingStatusClosed,
    propertyAffordablePrograms,
    waitlistV2Programs,
  } = useMemo(() => {
    const props = {
      accountThisPropertyForWaitlist: false,
      canAccessManageAffordableWaitlist: false,
      affordableWaitlistPropertySetting: null,
      isAffordableWaitlistPropertySettingStatusClosed: 'OPEN',
      propertyAffordablePrograms: [],
      waitlistV2Programs: [],
    };
    if (selectedProperty) {
      const { propertyClass, pap, setup } = selectedProperty;
      const isPropertyMixedOrAffordable = ['Affordable', 'Mixed'].includes(
        propertyClass?.name,
      );
      const isPropertyLayeredWithHUD = pap?.some(
        (map) => map.masterAffordableProgram?.name === 'HUD',
      );
      const isPropertyLayeredWithPH = pap?.some(
        (map) => map.masterAffordableProgram?.name === 'PH',
      );

      props.accountThisPropertyForWaitlist =
        isPropertyMixedOrAffordable &&
        (isPropertyLayeredWithHUD || isPropertyLayeredWithPH);
      props.affordableWaitlistPropertySetting = setup?.affordableWaitlist;
      // V1 waitlists only exist for HUD/PH properties; V2 runs on any affordable
      // program, so it only needs a Mixed or Affordable property.
      props.canAccessManageAffordableWaitlist = Boolean(
        setup?.affordableWaitlist &&
          (affordableWaitlistV2Flag
            ? isPropertyMixedOrAffordable
            : props.accountThisPropertyForWaitlist),
      );
      props.isAffordableWaitlistPropertySettingStatusClosed =
        setup?.affordableWaitlistStatus === 'CLOSED';
      props.propertyAffordablePrograms = pap?.map(
        (map) => map.masterAffordableProgram?.name,
      );
      // Use the programs explicitly configured for the V2 waitlist when available,
      // otherwise fall back to all property programs (same logic as AffordableSetup).
      props.waitlistV2Programs =
        setup?.waitlistV2?.programs != null
          ? setup.waitlistV2.programs
          : props.propertyAffordablePrograms ?? [];
    }
    return props;
  }, [selectedProperty, affordableWaitlistV2Flag]);

  return {
    affordableWaitlistFlag,
    hasAffordableWaitlistPermissionAdd,
    hasAffordableWaitlistPermissionReinstate,
    accountThisPropertyForWaitlist,
    canAccessManageAffordableWaitlist,
    affordableWaitlistPropertySetting,
    isAffordableWaitlistPropertySettingStatusClosed,
    propertyAffordablePrograms,
    waitlistV2Programs,
  };
};

export default useAffordableWaitlistValidations;
